import React from "react";
import { useNavigate } from "react-router-dom";
import { useMusicPlayer } from "../MusicPlayerContext";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Music, Play, ExternalLink, Flag, Settings, Shuffle } from "lucide-react";

// External music services — deep-linked
const EXTERNAL = [
  { name: "Spotify",      color: "#1DB954", url: "https://open.spotify.com/genre/workout-page" },
  { name: "Apple Music",  color: "#fa2d48", url: "https://music.apple.com/" },
  { name: "Amazon Music", color: "#25d1da", url: "https://music.amazon.com/" },
  { name: "YouTube Music", color: "#ff0000", url: "https://music.youtube.com/" },
  { name: "Pandora",      color: "#3668ff", url: "https://www.pandora.com/" },
  { name: "Tidal",        color: "#000000", url: "https://tidal.com/" },
];

export const MusicButton = ({ className = "", testIdPrefix = "hero" }) => {
  const navigate = useNavigate();
  const player = useMusicPlayer();

  const playFirstAnthem = () => {
    if (!player) return;
    const tracks = player.tracks || [];
    if (!tracks.length) {
      navigate("/music");
      return;
    }
    // Prefer a track with a known video_id, otherwise first track
    const idx = tracks.findIndex((t) => t.video_id);
    player.play(idx >= 0 ? idx : 0);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className={`bg-purple-700 hover:bg-purple-800 text-white h-8 px-3 text-xs ${className}`}
          data-testid={`${testIdPrefix}-music-player-btn`}
        >
          <Music className="mr-1 w-3 h-3" />
          Music Player
          <svg className="ml-1 w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">In-App</DropdownMenuLabel>
        <DropdownMenuItem onClick={playFirstAnthem} className="cursor-pointer" data-testid={`${testIdPrefix}-music-play-anthem`}>
          <Play className="mr-2 w-4 h-4 text-purple-600" />
          Play Union Anthems
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => player?.shuffleAll?.()} className="cursor-pointer" data-testid={`${testIdPrefix}-music-shuffle-all`}>
          <Shuffle className="mr-2 w-4 h-4 text-purple-600" />
          Shuffle All
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/music")} className="cursor-pointer" data-testid={`${testIdPrefix}-music-open-page`}>
          <Music className="mr-2 w-4 h-4 text-purple-600" />
          Open Music Library
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">Open External</DropdownMenuLabel>
        {EXTERNAL.map((p) => (
          <DropdownMenuItem key={p.name} asChild className="cursor-pointer" data-testid={`${testIdPrefix}-music-ext-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
            <a href={p.url} target="_blank" rel="noopener noreferrer">
              <Flag className="mr-2 w-4 h-4" style={{ color: p.color }} />
              <span className="flex-1">{p.name}</span>
              <ExternalLink className="w-3 h-3 text-slate-400" />
            </a>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/music-admin")} className="cursor-pointer text-slate-500" data-testid={`${testIdPrefix}-music-admin`}>
          <Settings className="mr-2 w-4 h-4" />
          Manage Playlist (Admin)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
